import { format } from 'date-fns'
import React from 'react'

import BarChart from '@/compoments/charts/BarChart'
import CircleProgressBar from '@/compoments/charts/CircleProgressBar'
import DoughnutChart from '@/compoments/charts/DoughnutChart'
import HorizontalBarChart from '@/compoments/charts/HorizontalBarChart'
import SelectBox from '@/compoments/common/SelectBox'
import type { ExecutivesDashboardQueryParams } from '@/services/executives/response.interface'
import type { ExecutivesDashboardData } from '@/types/executives'
import {
    getStartAndEndOfMonth,
    getStartAndEndOfWeek,
    getStartAndEndOfYear,
} from '@/utils/helpers'
import { optionsCharts } from '@/utils/selectsOptionsConst'

type setParamsType = [
    (
        | 'feedbackStartDate'
        | 'zoneStartDate'
        | 'revenueStartDate'
        | 'orderStartDate'
    ),
    'feedbackEndDate' | 'zoneEndDate' | 'revenueEndDate' | 'orderEndDate',
]

interface ChartsProps {
    data?: ExecutivesDashboardData
    setQueryParams: React.Dispatch<
        React.SetStateAction<ExecutivesDashboardQueryParams>
    >
}
const Charts = ({ data, setQueryParams }: ChartsProps) => {
    const setParams = (
        date: Date,
        types: setParamsType,
        value: string = 'week',
    ) => {
        const { startDate: startDateWeek, endDate: endDateWeek } =
            getStartAndEndOfWeek(date)
        const { startDate: startDateMonth, endDate: endDateMonth } =
            getStartAndEndOfMonth(date)
        const { startDate: startDateYear, endDate: endDateYear } =
            getStartAndEndOfYear(date)
        setQueryParams((prev) => ({
            ...prev,
            [types[0]]:
                value === 'week'
                    ? startDateWeek
                    : value === 'month'
                      ? startDateMonth
                      : startDateYear,
            [types[1]]:
                value === 'week'
                    ? endDateWeek
                    : value === 'month'
                      ? endDateMonth
                      : endDateYear,
        }))
    }

    const handelSelectChange = (value: string, type: setParamsType) => {
        setParams(new Date(), type, value)
    }

    return (
        <>
            <div className="col-12">
                <div className="row gutters-18">
                    <div className="col-lg-6">
                        <div className="card card-shadow card-chart h-full">
                            <div className="card-header">
                                <div className="card-chart-title text--xl">
                                    Revenue Analysis
                                </div>
                                <SelectBox
                                    wrapperClassName="select--transparent"
                                    options={optionsCharts}
                                    defaultValue={optionsCharts[0]}
                                    onChange={(option) => {
                                        if (!option?.value) return
                                        handelSelectChange(
                                            String(option.value),
                                            [
                                                'revenueStartDate',
                                                'revenueEndDate',
                                            ],
                                        )
                                    }}
                                />
                            </div>
                            <div className="card-body">
                                <div
                                    className="canvas-wrapper flex-auto"
                                    style={{ height: 215 }}
                                >
                                    <BarChart
                                        labels={
                                            data?.revenueAnalysis?.map(
                                                (entry) =>
                                                    format(
                                                        new Date(entry.date),
                                                        'MMM dd',
                                                    ),
                                            ) || []
                                        }
                                        values={
                                            data?.revenueAnalysis?.flatMap(
                                                (entry) =>
                                                    entry.services.map(
                                                        (service) => [
                                                            service.value,
                                                        ],
                                                    ),
                                            ) || []
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card card-shadow card-chart h-full">
                            <div className="card-header">
                                <div className="card-chart-title text--xl">
                                    Work Order Analysis
                                </div>
                                <SelectBox
                                    wrapperClassName="select--transparent"
                                    options={optionsCharts}
                                    defaultValue={optionsCharts[0]}
                                    onChange={(option) => {
                                        if (!option?.value) return
                                        handelSelectChange(
                                            String(option.value),
                                            ['orderStartDate', 'orderEndDate'],
                                        )
                                    }}
                                />
                            </div>
                            <div className="card-body">
                                <DoughnutChart
                                    labels={[
                                        `Open ${((data?.orderAnalysis?.open || 0) / (data?.orderAnalysis?.total || 1)) * 100}%`,
                                        `In Progress ${((data?.orderAnalysis?.inProgress || 0) / (data?.orderAnalysis?.total || 1)) * 100}%`,
                                        `Completed ${((data?.orderAnalysis?.completed || 0) / (data?.orderAnalysis?.total || 1)) * 100}%`,
                                    ]}
                                    values={Object.entries(
                                        data?.orderAnalysis || {},
                                    )
                                        .filter(([key]) => key !== 'total')
                                        .map(([key, value]) => value)}
                                    width={210}
                                    height={210}
                                    total={data?.orderAnalysis?.total}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="row gutters-18">
                    <div className="col-xl-4 col-md-6">
                        <div className="card card-shadow card-chart h-full">
                            <div className="card-header">
                                <div className="card-chart-title text--xl">
                                    Feedback
                                </div>
                                <SelectBox
                                    wrapperClassName="select--transparent"
                                    options={optionsCharts}
                                    defaultValue={optionsCharts[0]}
                                    onChange={(option) => {
                                        if (!option?.value) return
                                        handelSelectChange(
                                            String(option.value),
                                            [
                                                'feedbackStartDate',
                                                'feedbackEndDate',
                                            ],
                                        )
                                    }}
                                />
                            </div>
                            <div className="card-body">
                                <DoughnutChart
                                    labels={Object.entries(
                                        data?.feedbackStatistics || {},
                                    )
                                        .filter(([key]) => key !== 'total')
                                        .map(([key, value]) => key)}
                                    values={Object.entries(
                                        data?.feedbackStatistics || {},
                                    )
                                        .filter(([key]) => key !== 'total')
                                        .map(([key, value]) => value)}
                                    units="percents"
                                    total={data?.feedbackStatistics?.total}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-4 col-md-6">
                        <div className="card card-shadow card-chart h-full">
                            <div className="card-header">
                                <div className="card-chart-title text--xl">
                                    Jobs by Zone
                                </div>
                                <SelectBox
                                    wrapperClassName="select--transparent"
                                    options={optionsCharts}
                                    defaultValue={optionsCharts[0]}
                                    onChange={(option) => {
                                        if (!option?.value) return
                                        handelSelectChange(
                                            String(option.value),
                                            ['zoneStartDate', 'zoneEndDate'],
                                        )
                                    }}
                                />
                            </div>
                            <div className="card-body">
                                <HorizontalBarChart
                                    labels={Object.keys(
                                        data?.zoneStatistics || {},
                                    )
                                        .filter((key) => key !== 'total')
                                        .map((key) => key)}
                                    values={Object.entries(
                                        data?.zoneStatistics || {},
                                    )
                                        .filter(([key]) => key !== 'total')
                                        .map(([key, value]) => value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-4 col-md-6">
                        <div className="card card-shadow card-chart h-full">
                            <div className="card-header">
                                <div className="card-chart-title text--xl">
                                    Total Transactions
                                </div>
                                {/* <SelectBox
                                            wrapperClassName="select--transparent"
                                            options={optionsCharts}
                                        /> */}
                            </div>
                            <div className="card-body flex items-center">
                                <div className="pie-charts-group">
                                    <div className="pie-charts-group__item">
                                        <div className="pie-charts-group__chart">
                                            <CircleProgressBar
                                                progressColor={'#AC75F4'}
                                                percentageComplete={
                                                    +(
                                                        Number(
                                                            data?.transactions
                                                                ?.jobCompletionRate,
                                                        ) || 0
                                                    ).toFixed(0)
                                                }
                                            />
                                        </div>
                                        <div className="pie-charts-group__title">
                                            Job Completion Rate
                                        </div>
                                    </div>
                                    {/* <div className="pie-charts-group__item">
										<div className="pie-charts-group__chart">
											<CircleProgressBar
												progressColor={
													'#F54BA6'
												}
												percentageComplete={50}
											/>
										</div>
										<div className="pie-charts-group__title">
											First-Time Fix Rate
										</div>
									</div> */}
                                    <div className="pie-charts-group__item">
                                        <CircleProgressBar
                                            progressColor={'#FF4433'}
                                            percentageComplete={
                                                +(
                                                    Number(
                                                        data?.transactions
                                                            ?.averageJobDuration,
                                                    ) || 0
                                                ).toFixed(0)
                                            }
                                        />
                                        <div className="pie-charts-group__title">
                                            Average Job Duration
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Charts
